import { Weddings } from '@bridebook/models';
import { Events } from '@bridebook/models/source/models/Weddings/Events';
import {
  IGuest,
  IGuest_Address,
  IGuest_Event,
  IGuest_Invitation,
  Rsvp,
} from '@bridebook/models/source/models/Weddings/Guests.types';
import { TranslatedError } from '@bridebook/toolbox/src';
import { GuestlistActionTypes } from '@bridebook/web/lib/guestlist/action-types';
import { addedGuestAnalytics } from '@bridebook/web/lib/guestlist/analytics/actions';
import type {
  GuestDietaryOptionsType,
  InvitationTypes,
} from '@bridebook/web/lib/guestlist/constants';
import { getGuestsByGroupId } from '@bridebook/web/lib/guestlist/utils';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import type { AddressComponentForm } from '@bridebook/web/lib/search/types';
import { TrackingEvent } from '@bridebook/web/lib/track-utils/tracking-event';
import type { IDeps } from '@bridebook/web/lib/types';
import { parseDate } from '@bridebook/web/lib/utils/parse-date';
import { ADD_GUEST_TO_MAIN_EVENT_BY_DEFAULT_ACTIVATE_DATE } from '@bridebook/web/lib/events/constants';
import { GuestlistAnalyticsActionTypes } from '@bridebook/web/lib/guestlist/analytics/action-types';

export const editNewGuest = <K extends keyof Required<IGuest>>(
  name: K,
  value: Required<IGuest>[K],
) => ({
  type: GuestlistActionTypes.EDIT_GUEST,
  payload: { name, value, method: 'popover' },
});

export const editGuestContacts = <K extends keyof Required<IGuest>['contacts']>(
  name: K,
  value: Required<IGuest>['contacts'][K],
) => ({
  type: GuestlistActionTypes.EDIT_GUEST_CONTACTS,
  payload: { name, value },
});

export const editGuestAddress = <K extends keyof IGuest_Address>(
  name: K,
  value: IGuest_Address[K],
) => ({
  type: GuestlistActionTypes.EDIT_GUEST_ADDRESS,
  payload: { name, value },
});

export const editGuestRsvpEventsById = (events: IGuest_Invitation['events']) => ({
  type: GuestlistActionTypes.EDIT_GUEST_RSVP_EVENTS_BY_ID,
  payload: { events },
});

/**
 * What a by-id edit may change — a field that is omitted (or `null`) leaves
 * the guest's value untouched. `rsvp` writes the attendance directly
 * ('pending' clears it back to no answer); strings and `dietaryOptions`
 * replace the guest's current value. `linkedToGuestId` moves the guest into
 * another guest's group (their partner/family lead); an empty string detaches
 * them from their group.
 */
export type GuestByIdUpdates = {
  name?: string | null;
  rsvp?: Rsvp | null;
  invitationType?: InvitationTypes | null;
  dietaryOptions?: GuestDietaryOptionsType[] | null;
  category?: { id?: string; value?: string } | null;
  email?: string | null;
  phone?: string | null;
  address?: {
    street?: string[] | null;
    city?: string | null;
    postalCode?: string | null;
  } | null;
  table?: string | null;
  notes?: string | null;
  linkedToGuestId?: string | null;
};

// Edits guests addressed by id in one batch; the epic resolves each guest and
// writes the changed fields. `guestName` is the current name, display-only —
// the AI consent card shows it.
export const updateGuestsById = (payload: {
  guests: Array<{ guestId: string; guestName: string; updates: GuestByIdUpdates }>;
}) => ({
  type: GuestlistActionTypes.UPDATE_GUESTS_BY_ID as const,
  payload,
});

export const editGuest =
  (guest: IGuest, method: string) =>
  ({ dispatch }: IDeps) => {
    dispatch({
      type: GuestlistAnalyticsActionTypes.CLICKED_EDIT_GUEST,
      payload: { method, guest },
    });
    return {
      type: GuestlistActionTypes.EDIT_GUEST_FORM,
      payload: { guest },
    };
  };

export const guestAddressManualEnter = (show: boolean) => ({
  type: GuestlistActionTypes.GUESTLIST_ADDRESS_MANUAL_ENTER,
  payload: show,
});

export const guestDiscardChangesAnalyticsAction = () => ({
  type: GuestlistActionTypes.CANCEL_EDIT_GUEST_ANALYTICS,
});

export const cancelGuestEdit =
  () =>
  ({ dispatch }: IDeps) => {
    dispatch(guestAddressManualEnter(false));
    dispatch(guestDiscardChangesAnalyticsAction());
    return {
      type: GuestlistActionTypes.CANCEL_EDIT_GUEST,
    };
  };

export const makeHead =
  (guest: IGuest) =>
  async ({ getState }: IDeps) => {
    const state = getState();
    const allGuests = state.guestlist.list;
    const i18n = getI18n();

    if (!guest.head) {
      throw new TranslatedError(
        'Guest is already a Group Head',
        i18n.t('guestlist:errorGuestGroupHead'),
      );
    }

    const otherGuestsInGroup = getGuestsByGroupId(guest.head, allGuests).filter(
      (g) => g.id !== guest.id,
    );

    const Guests = Weddings._.getById(state.weddings.profile.id).Guests;

    // Begin batch operation
    Guests.begin();

    // Make guest the Head of the group by removing his reference to any other Head,
    // and add references in other guests in the group.
    await Guests.getById(guest.id).set({
      head: null,
    });

    for (const otherGuest of otherGuestsInGroup) {
      await Guests.getById(otherGuest.id).set({
        head: guest.id,
      });
    }

    await Guests.commit();

    return {
      type: GuestlistActionTypes.MAKE_GROUP_HEAD,
    };
  };

export const setGuestLocation = (location: AddressComponentForm<string>) => ({
  type: GuestlistActionTypes.SET_GUEST_LOCATION,
  payload: location,
});

export const addPlusOne = (plusOne: IGuest) => ({
  type: GuestlistActionTypes.ADD_PLUS_ONE,
  payload: plusOne,
});

export const removePlusOne = (id: string) => ({
  type: GuestlistActionTypes.REMOVE_PLUS_ONE,
  payload: id,
});

export const saveCustomPlusOne =
  (plusOne: IGuest) =>
  ({ getState, dispatch, cordovaTracker }: IDeps) => {
    const state = getState();
    const weddingId = state.weddings.profile.id;
    const mainGuest = state.guestlist.guest;

    const shouldApplyMainEventInvitation =
      parseDate(state.weddings.profile.createdAt) >
        ADD_GUEST_TO_MAIN_EVENT_BY_DEFAULT_ACTIVATE_DATE &&
      !!mainGuest.invitation?.events?.[Events.MAIN_EVENT];

    const plusOneToSave = shouldApplyMainEventInvitation
      ? {
          ...plusOne,
          invitation: {
            ...plusOne.invitation,
            events: {
              ...plusOne.invitation?.events,
              [Events.MAIN_EVENT]: { rsvp: Rsvp.PENDING },
            },
          },
        }
      : plusOne;

    Weddings._.getById(weddingId).Guests.push(plusOneToSave.id).create(plusOneToSave);

    cordovaTracker.track(TrackingEvent.AddedGuest);
    dispatch(addedGuestAnalytics({ edit: true, method: 'plus1', guest: plusOneToSave }));

    return {
      type: GuestlistActionTypes.SAVE_CUSTOM_PLUS_ONE,
      payload: plusOneToSave,
    };
  };

export const createPlusOneAndAssign =
  (plusOne: IGuest) =>
  ({ dispatch }: IDeps) => {
    dispatch(saveCustomPlusOne(plusOne));
    dispatch(addPlusOne(plusOne));

    return {
      type: GuestlistActionTypes.CREATE_PLUS_ONE_AND_ASSIGN,
    };
  };

export const editInvitationType = (invitation: InvitationTypes) => ({
  type: GuestlistActionTypes.EDIT_GUEST_INVITATION_TYPE,
  payload: invitation,
});

export const editRsvpStatus = (status: IGuest_Event['attending']) => ({
  type: GuestlistActionTypes.EDIT_GUEST_RSVP_STATUS,
  payload: status,
});
